// 机票模块静态数据与工具。
// 机场目录由后端 GET /flights/airports 提供（flight/airports.py，约 490 个），
// 这里只保留少量热门航点作为兜底，用于接口返回前的首屏渲染。
import { getFlightAirports } from '../../services/flightService';

const FALLBACK_AIRPORTS = [
  { code: 'PEK', city_zh: '北京', city_en: 'Beijing', airport_zh: '首都国际机场', tz: 'Asia/Shanghai', country: 'CN' },
  { code: 'PVG', city_zh: '上海', city_en: 'Shanghai', airport_zh: '浦东国际机场', tz: 'Asia/Shanghai', country: 'CN' },
  { code: 'CAN', city_zh: '广州', city_en: 'Guangzhou', airport_zh: '白云国际机场', tz: 'Asia/Shanghai', country: 'CN' },
  { code: 'SZX', city_zh: '深圳', city_en: 'Shenzhen', airport_zh: '宝安国际机场', tz: 'Asia/Shanghai', country: 'CN' },
  { code: 'HKG', city_zh: '香港', city_en: 'Hong Kong', airport_zh: '国际机场', tz: 'Asia/Hong_Kong', country: 'HK' },
  { code: 'LAX', city_zh: '洛杉矶', city_en: 'Los Angeles', airport_zh: '洛杉矶国际机场', tz: 'America/Los_Angeles', country: 'US' },
  { code: 'JFK', city_zh: '纽约', city_en: 'New York', airport_zh: '肯尼迪国际机场', tz: 'America/New_York', country: 'US' },
  { code: 'SFO', city_zh: '旧金山', city_en: 'San Francisco', airport_zh: '旧金山国际机场', tz: 'America/Los_Angeles', country: 'US' },
  { code: 'SEA', city_zh: '西雅图', city_en: 'Seattle', airport_zh: '塔科马国际机场', tz: 'America/Los_Angeles', country: 'US' },
  { code: 'ORD', city_zh: '芝加哥', city_en: 'Chicago', airport_zh: '奥黑尔国际机场', tz: 'America/Chicago', country: 'US' },
];

// 运行时目录：接口返回后原地替换，airportByCode 等工具无需感知加载状态
export let AIRPORTS = FALLBACK_AIRPORTS;

const CACHE_KEY = 'flight_airports_v1';
const CACHE_TTL_MS = 24 * 3600 * 1000;
let loadPromise = null;

/** 拉取完整机场目录（内存 + localStorage 双层缓存），返回目录数组。 */
export function loadAirports() {
  if (loadPromise) return loadPromise;

  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const { at, data } = JSON.parse(raw);
      if (Date.now() - at < CACHE_TTL_MS && Array.isArray(data) && data.length) {
        AIRPORTS = data;
      }
    }
  } catch (e) {
    // localStorage 不可用（隐私模式等）时忽略，走网络
  }

  loadPromise = getFlightAirports()
    .then((resp) => {
      const list = resp?.airports;
      if (Array.isArray(list) && list.length) {
        AIRPORTS = list;
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data: list }));
        } catch (e) { /* 超配额则不缓存，不影响使用 */ }
      }
      return AIRPORTS;
    })
    .catch(() => AIRPORTS); // 接口失败时保留兜底目录，搜索仍可用

  return loadPromise;
}

export const airportByCode = (code) => AIRPORTS.find((a) => a.code === code) || { code, city_zh: code, city_en: code, airport_zh: code, tz: 'UTC', country: '' };

// 单人价格拆解。Duffel 的 base_amount 是**不含税**票价，tax_amount 另加，
// 二者相加才等于 total（实测 220/220 条成立）。所以绝不能写成"含税费 X"——
// 那读起来像是税已经包含在票价里，而有 2% 的报价税费甚至高于票价，更显荒谬。
export const priceBreakdown = (offer) => {
  const fare = offer.price_per_adult || 0;
  const tax = offer.tax_fee || 0;
  return { fare, tax, perAdult: fare + tax, currency: offer.currency };
};

// 中转点及各自的等待时长。
// 注意这里必须用真实时间差（Date 差值），不能像展示时刻那样截字符串——
// 相邻两段跨时区，"上一段落地"到"下一段起飞"的间隔只有按绝对时刻算才对。
export const layovers = (segments = []) =>
  segments.slice(0, -1).map((s, i) => {
    const next = segments[i + 1];
    const arrive = new Date(s.arr_time).getTime();
    const depart = new Date(next.dep_time).getTime();
    const minutes = Number.isNaN(arrive) || Number.isNaN(depart)
      ? null
      : Math.round((depart - arrive) / 60000);
    return {
      code: s.arr_airport,
      city: airportByCode(s.arr_airport).city_zh,
      minutes,
      // 落地机场与下一段起飞机场不同 → 要自行转场（如虹桥→浦东、成田→羽田，均相距数十公里）
      nextCode: next.dep_airport,
      changesAirport: s.arr_airport !== next.dep_airport,
    };
  });

// 中转摘要："直飞" / "经 上海 中转 2h30m" / "经 首尔、东京 2 次中转"
export const layoverSummary = (segments = []) => {
  const stops = layovers(segments);
  if (!stops.length) return '直飞';
  if (stops.length === 1) {
    const { city, minutes } = stops[0];
    return minutes ? `经 ${city} 中转 ${formatDuration(minutes)}` : `经 ${city} 中转`;
  }
  return `经 ${stops.map((s) => s.city).join('、')} ${stops.length} 次中转`;
};

// 中转风险：换机场、或转机时间过短。换机场时一并给出可用时间，让用户自己判断来不来得及。
export const SHORT_LAYOVER_MIN = 90;

export const layoverWarnings = (segments = []) =>
  layovers(segments).flatMap((s) => {
    const dur = s.minutes !== null ? formatDuration(s.minutes) : null;
    if (s.changesAirport) {
      const base = `需转场：${airportDetailLabel(s.code)} → ${airportDetailLabel(s.nextCode)}`;
      return [dur ? `${base}，可用 ${dur}` : base];
    }
    if (s.minutes !== null && s.minutes < SHORT_LAYOVER_MIN) {
      return [`${s.city}转机仅 ${dur}，时间较紧`];
    }
    return [];
  });

// 退改条件 → 展示用的 { level, label, tone }。
// 四态而非布尔：航司约 40% 的报价压根不返回退改信息，这种「未知」必须如实标出，
// 不能显示成「不可退」——全场最低价那批恰恰大量落在未知档。
const TONES = {
  free: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  penalty: 'bg-amber-50 text-amber-700 border-amber-200',
  no: 'bg-gray-100 text-gray-500 border-gray-200',
  unknown: 'bg-gray-50 text-gray-400 border-gray-200',
};

export const describeFareCondition = (cond, kind) => {
  const noun = kind === 'change' ? '改期' : '退票';
  if (!cond || cond.allowed === null || cond.allowed === undefined) {
    return { level: 'unknown', label: `${noun}以航司规定为准`, tone: TONES.unknown };
  }
  if (!cond.allowed) {
    return { level: 'no', label: `不可${noun}`, tone: TONES.no };
  }
  if (!cond.penalty_amount) {
    return { level: 'free', label: `免费${noun}`, tone: TONES.free };
  }
  const fee = formatMoney(cond.penalty_amount, cond.penalty_currency);
  return { level: 'penalty', label: `${noun}费 ${fee}`, tone: TONES.penalty };
};

// 同城多机场必须能区分：上海有虹桥(SHA)和浦东(PVG)，东京有成田(NRT)和羽田(HND)，
// 只写"上海 SHA / 上海 PVG"看不出差别，而换机场是要跨越几十公里的。
export const airportDetailLabel = (code) => {
  const a = airportByCode(code);
  const short = (a.airport_zh || '').replace(/国际机场$|机场$/, '');
  return short && short !== a.city_zh ? `${a.city_zh}·${short} ${code}` : `${a.city_zh} ${code}`;
};

export const airportLabel = (code) => {
  const a = airportByCode(code);
  return `${a.city_zh} ${code}`;
};

// 时长（分钟）→ "12h50m"
export const formatDuration = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h${m > 0 ? `${m}m` : ''}` : `${m}m`;
};

// 航班时刻一律按「机场当地时间」展示，不能转成浏览器时区——
// 后端给的是带偏移的 ISO（如 "2026-10-08T06:29:00-07:00" 表示洛杉矶当地 6:29），
// 用 new Date().getHours() 会在 UTC+8 的浏览器里显示成 21:29，落地时刻就错了。
// 直接从字符串截取即可，偏移量本身已经表达了当地时间。
const ISO_PARTS = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

// ISO → 机场当地 "10-15 21:30"
export const formatTime = (iso) => {
  const m = ISO_PARTS.exec(iso || '');
  return m ? `${m[2]}-${m[3]} ${m[4]}:${m[5]}` : (iso || '');
};

// ISO → 机场当地小时数（0-23），筛选与展示共用同一口径
export const localHour = (iso) => {
  const m = ISO_PARTS.exec(iso || '');
  return m ? Number(m[4]) : null;
};

// ISO → 仅时刻 "14:15"（用于报价有效期这类当下就到期的时间）
export const formatClock = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const formatDate = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || '';
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

// 订单状态徽章映射
export const ORDER_STATUS = {
  pending: { label: '待确认', badge: 'bg-amber-100 text-amber-800 border-amber-200' },
  confirmed: { label: '已确认', badge: 'bg-sky-100 text-sky-800 border-sky-200' },
  paid: { label: '已付款', badge: 'bg-violet-100 text-violet-800 border-violet-200' },
  issued: { label: '已出票', badge: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cancelled: { label: '已取消', badge: 'bg-gray-100 text-gray-600 border-gray-200' },
  failed: { label: '失败', badge: 'bg-red-100 text-red-700 border-red-200' },
};

export const CABIN_LABEL = { econ: '经济舱', business: '公务舱' };

// 报价币种随供应商变化（Duffel 国际线报 USD），不能再硬编码 ¥
const CURRENCY_SYMBOL = { CNY: '¥', USD: '$', HKD: 'HK$', EUR: '€', GBP: '£', JPY: '¥' };

export const formatMoney = (amount, currency = 'CNY') => {
  if (amount === null || amount === undefined || amount === '') return '-';
  const code = (currency || 'CNY').toUpperCase();
  const num = Number(amount).toLocaleString('zh-CN');
  const symbol = CURRENCY_SYMBOL[code];
  return symbol ? `${symbol}${num}` : `${code} ${num}`;
};

export const PAX_LABEL = { adult: '成人', child: '儿童', infant: '婴儿' };

export const todayStr = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};
